import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridColDef, GridToolbarQuickFilter } from "@mui/x-data-grid";
import "./BniOverview.css";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";

import { FetchBinReport, FetchDLCReport } from "../../ApiServices/ApiRequests";
import {
  BinOverviewReportInfoResponse,
  BinReportInfo,
  SitesInfo,
} from "../../Models/Models";
import { columns, getRowClassName } from "./BinOverviewUtiles";

import {
  useWasteManagement,
  WasteManagementType,
} from "../../ContextAPI/WasteManagementContext";
import dayjs from "dayjs";
import ExportBinPage from "./ReportExport/BinExport";
import HeaderComponent from "../HeaderComponent/HeaderComponent";
import DLCMonitoringCard from "./ReportCards/DLCReportCard";
import ExportDLCReport from "./ReportExport/DLCExport";
import Loading from "../../Controller/LoadingController/Loading";
import { useMasterData } from "../../ContextAPI/MasterContext";
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import moment from "moment";
import { RetailType } from "../../ContextAPI/RetailContext";
import AppColors from "../../Utils/AppColor";
import ImageNotFound from "../../Assests/ImageNotFound.png";

export default function DLCReport() {
  const { masterState } = useMasterData();
  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();
  const { siteData, pageSize, pageNo } = wasteManagementState;
  const [loading, setloading] = useState<boolean>(false);

  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value: BinReportInfo[] | number | boolean
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const handleBulkUpdate = useCallback(
    (value: Partial<WasteManagementType>) => {
      dispatchWasteManagement({ type: "BULK_UPDATE", value });
    },
    [dispatchWasteManagement]
  );

  const { t } = useTranslation();

  const fetchData = async (
    siteCode: string,
    pageNo: number,
    pageSize: number,
    fromDate: string,
    groupId: string
  ): Promise<void> => {
    try {
      setloading(true);
      const data: BinOverviewReportInfoResponse = await FetchDLCReport(
        siteCode,
        pageNo,
        pageSize,
        fromDate,
        groupId
      );
      const reportData =
        data?.content && data?.content?.length > 0
          ? {
              siteData:
                data.content.map((item, index) => ({
                  ...item,
                  id: index,
                })) || [], // Ensure siteData is an empty array if report is undefined
            }
          : {
              siteData: [], // Set siteData as an empty array if content is empty
            };

      var wasteDataSet: Partial<WasteManagementType> = {};
      wasteDataSet.siteData = reportData.siteData;
      wasteDataSet.totalElements = data.totalElements;

      handleBulkUpdate(wasteDataSet);
    } catch (error) {
      console.error("Error loading site data", error);
    } finally {
      setloading(false);
    }
  };

  useEffect(() => {
    const formattedDate: string = masterState.reportSelectedDate
      ? masterState.reportSelectedDate.format("YYYY-MM-DD")
      : "";

    if (masterState.selectedSiteCode.length > 0) {
      fetchData(
        masterState.selectedSiteCode,
        wasteManagementState.pageNo,
        wasteManagementState.pageSize,
        formattedDate,
        masterState.selectedGroupCode
      );
    }
  }, [
    masterState.selectedSiteCode,
    masterState.selectedGroupCode,
    masterState.reportSelectedDate,
    wasteManagementState.pageNo,
    wasteManagementState.pageSize,
  ]);

  const handleViewClick = (selectedData: BinReportInfo) => {
    var updatedata: Partial<WasteManagementType> = {
      dicSelectedReportData: selectedData,
      isDicSelectedImageOpen: true,
    };
    handleBulkUpdate(updatedata);
  };

  const handleClose = () => {
    var updatedata: Partial<WasteManagementType> = {
      dicSelectedReportData: null,
      isDicSelectedImageOpen: false,
    };
    handleBulkUpdate(updatedata);
  };

  const columData: GridColDef[] = useMemo(
    () => [
      {
        field: "deviceName",
        headerName: t("global.deviceName"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 1,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              textAlign: "center",
              width: "100%",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "location",
        headerName: t("global.Location"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 1,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              textAlign: "center",
              width: "100%",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "groupName",
        headerName: t("global.GroupName"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 1,
        // renderCell: (params) => (
        //   <Typography
        //     variant="body2"
        //     sx={{
        //       whiteSpace: "pre-wrap",
        //       wordWrap: "break-word",
        //       overflowWrap: "break-word",
        //       textAlign: "center",
        //       width: "100%",
        //       alignItems: "center",
        //     }}
        //   >
        //     {params.value}
        //   </Typography>
        // ),
      },
      {
        field: "status",
        headerName: t("global.status"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
        renderCell: (params) => {
          let color;
          switch (params.value) {
            case "Clean":
              color = "green";
              break;
            case "Critical":
              color = "red";
              break;
            case "Moderate":
              color = "orange";
              break;
            default:
              color = "inherit";
          }

          return <span style={{ color }}>{params.value}</span>;
        },
      },
      {
        field: "level",
        headerName: t("global.FillLevel(%)"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
        renderCell: (params) =>
          params.value || params.value === 0 ? `${params.value}%` : "-",
      },
      {
        field: "lastCollectedDate",
        headerName: t("global.LastCollected"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },

      {
        field: "nextCollectionDate",
        headerName: t("global.NextCollected"),
        width: 0.55,
        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },

      {
        field: "image",
        headerName: t("global.image"),
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Chip
            label="View"
            clickable
            color="primary"
            onClick={() => handleViewClick(params.row)}
          />
        ),
        width: 0.55,
        flex: 0.5,
      },
    ],
    []
  );
  return (
    <>
      <Box sx={{ width: "100%", mt: 10.5 }}>
        <HeaderComponent />
        <DLCMonitoringCard />
        <ExportDLCReport />
        {loading ? (
          <Loading loading={loading} type={`bars`} backDrop={false} />
        ) : (
          <Box className="data-grid-container">
            <DataGrid
              rows={siteData}
              disableAutosize
              disableRowSelectionOnClick
              disableColumnResize
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              disableColumnSorting
              disableColumnMenu
              localeText={{
                noRowsLabel: "No Data Found",
              }}
              columns={columData}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                },
              }}
              getRowClassName={getRowClassName}
              paginationMode="server" // Enable server-side pagination
              paginationModel={{ pageSize, page: pageNo }} // Use paginationModel
              onPaginationModelChange={(model) => {
                handleInputChange("pageSize", model.pageSize);
                handleInputChange("pageNo", model.page);
              }}
              rowCount={wasteManagementState.totalElements}
              pageSizeOptions={[5, 10, 25, 50, 75, 100]}
            />
          </Box>
        )}

        <Typography
          variant="body2"
          textAlign="center"
          sx={{ marginTop: 10, color: "#000000", marginBottom: 1 }}
        >
          {t("global.copyRight")} © {moment().year()},{" "}
          {t("global.copyRightText")}
        </Typography>
      </Box>
      {wasteManagementState?.dicSelectedReportData && (
        <Dialog
          open={wasteManagementState.isDicSelectedImageOpen}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {wasteManagementState?.dicSelectedReportData?.deviceName}
          </DialogTitle>
          <IconButton
            style={{ position: "absolute", top: "5px", right: "5px" }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <img
              src={
                wasteManagementState?.dicSelectedReportData?.image ??
                ImageNotFound
              }
              alt="dic Image"
              style={{
                width: "100%",
                maxHeight: 260,
                objectFit: "contain",
                minWidth: "100%", // Optional
                display: "block", // Avoid inline-block spacing issues
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
